import React from 'react';
import {
  Box,
  Typography,
  ListItem,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TimerProgressCircle from './TimerComponet';
import { getPriorityColor } from '../utils/priorityColors';

/**
 * Unified component for displaying assigned or completed tasks
 * @param {Object} props
 * @param {string} props.title - The task title
 * @param {(string|Object)[]} props.typographyList - Text rows to display after title
 * @param {number} props.duration - Duration in seconds
 * @param {number} props.workingTime - Working time in seconds (optional)
 * @param {Function} props.onMenuOpen - Optional callback for menu open
 * @param {Function} props.onClick - Optional click handler for the item
 * @param {string} props.type - 'assigned' or 'completed'
 */
const TaskItem = ({
  id,
  title,
  typographyList,
  duration,
  workingTime = 0,
  onMenuOpen,
  onClick,
  type,
  menuButtonId,
}) => {
  // Format time in seconds to MM:SS
  const formatTime = (seconds) => {
    if (!seconds) return '00:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Calculate progress for the circle
  let progressValue = 100;
  let progressLabel = '';
  if (type === 'assigned') {
    // For assigned tasks, show remaining time
    const remainingTime = duration - workingTime;
    progressLabel = formatTime(remainingTime > 0 ? remainingTime : duration);
  } else if (type === 'completed') {
    // For completed tasks, show efficiency
    
    progressLabel = formatTime(workingTime);
  }
  const efficiency = duration > 0 ? Math.max(0, (1 - (workingTime / duration)) * 100) : 0;
  progressValue = efficiency;
  const handleMenuClick = (event) => {
    event.stopPropagation();
    if (onMenuOpen) onMenuOpen(event.currentTarget);
  };

  const handleItemClick = () => {
    if (onClick) onClick();
  };
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const size = isSmall ? 60 : isMedium ? 80 : 120;
  const thickness = isSmall ? 4 : 6;
  return (
    <ListItem
      id={id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 2,
        px: 2,
        borderRadius: 8,
        border: '4px solid',
        borderColor: 'green.main',
        mb: 1,
        background: 'linear-gradient(90deg, #0A0F0D 60%, #1d2a25ff 100%)',
        boxShadow: '0px 6px 15px #FF2D95',
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        width: '100%',
        minWidth: 0,
        gap: 2,
      }}
      onClick={handleItemClick}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: '1 1 auto', minWidth: 0 }}>
        <Typography
          variant="body1"
          component="div"
          sx={{ mb: 1, maxWidth: '100%', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: 'column', maxWidth: '100%', minWidth: 0 }}>
          {typographyList.map((item, index) => {
            const row = typeof item === 'string' ? { text: item } : item;
            const color = row.userPriority === undefined
              ? (theme.palette.text.pink || theme.palette.secondary.main)
              : getPriorityColor(row.userPriority, theme);
            return (
              <Typography
                key={index}
                variant="body2"
                textAlign="left"
                sx={{
                  color,
                  maxWidth: '100%',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {row.text}
              </Typography>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', flexShrink: 0 }}>
        <Typography variant="body2" color="text.pink" textAlign="center" sx={{ mb: 1, mt: 4, mr: 2 }}>
          Time
        </Typography>
        <TimerProgressCircle
          value={progressValue}
          label={progressLabel}
          size={size}
          thickness={thickness}
          textVariant="body2"
        />
      </Box>

      {onMenuOpen && (
        <IconButton
          id={menuButtonId}
          size="small"
          onClick={handleMenuClick}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
          }}
        >
          <MoreVertIcon color="green" />
        </IconButton>
      )}
    </ListItem>
  );
};

export default TaskItem;
